import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Elements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import debounce from "lodash/debounce";
import "./Checkout.css";

const BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://127.0.0.1:8000"
    : "https://bonobohope-chazselph.pythonanywhere.com";

const getStripePromise = async () => {
  try {
    const response = await fetch(`${BASE_URL}/api/stripe-publishable-key/`);
    const data = await response.json();
    if (!response.ok) throw new Error("Failed to fetch Stripe key");
    return loadStripe(data.publishableKey);
  } catch (err) {
    console.error("Error fetching Stripe publishable key:", err);
    throw err;
  }
};

const usStates = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const CheckoutStep2 = ({ setCart }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [billingAddress, setBillingAddress] = useState({
    address1: "",
    address2: "",
    city: "",
    region: "Alabama",
    zip: "",
    country: "US",
  });
  const [sameAsShipping, setSameAsShipping] = useState(false);

  const {
    cart,
    shippingOptions,
    taxCalculation,
    personalInfo,
    shippingAddress,
  } = state || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (
    !cart ||
    !shippingOptions ||
    !taxCalculation ||
    !personalInfo ||
    !shippingAddress
  ) {
    return (
      <div className="checkout-page">
        Invalid checkout data. Please start over.
      </div>
    );
  }

  const handleBillingChange = (e) => {
    setBillingAddress({ ...billingAddress, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setSameAsShipping(checked);
    if (checked) {
      setBillingAddress({ ...shippingAddress });
    }
  };

  const validateBillingFields = () => {
    const { address1, city, region, zip } = billingAddress;
    const zipRegex = /^\d{5}(-\d{4})?$/;

    if (!address1 || address1.length < 3)
      return "Address Line 1 must be at least 3 characters.";
    if (!city || city.length < 2) return "City must be at least 2 characters.";
    if (!region) return "State is required.";
    if (!zip || !zipRegex.test(zip))
      return "Invalid ZIP code (e.g., 12345 or 12345-6789).";

    return true;
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const shipping = shippingOptions?.standard?.cost || 0;
    const tax = taxCalculation ? taxCalculation.tax_amount_exclusive / 100 : 0;
    return (subtotal + shipping + tax).toFixed(2);
  };

  const debouncedBuyNow = debounce(async () => {
    if (!stripe || !elements) {
      setPaymentError("Payment system not initialized.");
      return;
    }
    const validationResult = validateBillingFields();
    if (validationResult !== true) {
      setPaymentError(validationResult);
      return;
    }
    setProcessing(true);
    setPaymentError(null);
    const total = calculateTotal();
    const taxAmount = taxCalculation
      ? taxCalculation.tax_amount_exclusive / 100
      : 0;

    try {
      const payload = {
        amount: parseFloat(total),
        first_name: personalInfo.firstName,
        last_name: personalInfo.lastName,
        email: personalInfo.email,
        cart: cart,
        shipping_cost: parseFloat(shippingOptions.standard.cost),
        tax_amount: parseFloat(taxAmount),
        shipping_address: {
          line1: shippingAddress.address1,
          line2: shippingAddress.address2 || "",
          city: shippingAddress.city,
          state: shippingAddress.region,
          postal_code: shippingAddress.zip,
          country: shippingAddress.country,
        },
      };

      const response = await fetch(
        `${BASE_URL}/api/create-purchase-payment-intent/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const { clientSecret } = await response.json();
      if (!response.ok) throw new Error("Failed to create payment intent");

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: {
            name: `${personalInfo.firstName} ${personalInfo.lastName}`,
            email: personalInfo.email,
            address: {
              line1: billingAddress.address1,
              line2: billingAddress.address2,
              city: billingAddress.city,
              state: billingAddress.region,
              postal_code: billingAddress.zip,
              country: billingAddress.country,
            },
          },
        },
      });

      if (result.error) {
        setPaymentError(result.error.message);
        navigate("/failure", {
          state: { error: "Payment failed. Please try again." },
        });
      } else {
        // Payment succeeded, clear the cart immediately
        setCart([]);

        const emailPayload = {
          payment_intent_id: result.paymentIntent.id,
          cart,
          shipping_cost: shippingOptions.standard.cost,
          tax_amount: taxAmount,
          total,
          customer_email: personalInfo.email,
          customer_name: `${personalInfo.firstName} ${personalInfo.lastName}`,
          shipping_address: `${shippingAddress.address1}, ${
            shippingAddress.address2 ? shippingAddress.address2 + ", " : ""
          }${shippingAddress.city}, ${shippingAddress.region} ${
            shippingAddress.zip
          }, ${shippingAddress.country}`,
        };

        try {
          const emailResponse = await fetch(
            `${BASE_URL}/api/send-order-email/`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(emailPayload),
            }
          );
          if (!emailResponse.ok) {
            const errorData = await emailResponse.json();
            console.error("Failed to send order email:", errorData);
            throw new Error("Failed to send order email");
          }
          navigate("/success", { state: { orderDetails: emailPayload } });
        } catch (emailErr) {
          console.error("Email error:", emailErr);
          navigate("/email-failure", { state: { orderDetails: emailPayload } });
        }
      }
    } catch (err) {
      console.error("Error in handleBuyNow:", err);
      setPaymentError(
        "An error occurred during payment. Please try again or contact support."
      );
      navigate("/failure", { state: { error: "An error occurred" } });
    } finally {
      setProcessing(false);
    }
  }, 1000);

  const handleBuyNow = () => {
    debouncedBuyNow();
  };

  return (
    <div className="checkout-page">
      <div className="checkout-form-container">
        <h2 className="checkout-title">Checkout</h2>
        <form className="checkout-form">
          <div className="form-section">
            <h3 className="section-title">Payment Information</h3>
            <p className="secure-note">
              This is a secure SSL encrypted payment.
            </p>
            <h4 className="subsection-title">Billing Address</h4>
            <div className="input-group">
              <label className="input-label">
                Same as Shipping Address
                <input
                  type="checkbox"
                  checked={sameAsShipping}
                  onChange={handleCheckboxChange}
                  className="checkout-checkbox"
                />
              </label>
            </div>
            <div className="input-group">
              <label htmlFor="billingAddress1" className="input-label">
                Address Line 1 <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                id="billingAddress1"
                name="address1"
                className="checkout-input"
                value={billingAddress.address1}
                onChange={handleBillingChange}
                placeholder="Enter address"
                required
                disabled={sameAsShipping}
              />
            </div>
            <div className="input-group">
              <label htmlFor="billingAddress2" className="input-label">
                Address Line 2
              </label>
              <input
                type="text"
                id="billingAddress2"
                name="address2"
                className="checkout-input"
                value={billingAddress.address2}
                onChange={handleBillingChange}
                placeholder="Apt, suite, etc. (optional)"
                disabled={sameAsShipping}
              />
            </div>
            <div className="input-group">
              <label htmlFor="billingCity" className="input-label">
                City <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                id="billingCity"
                name="city"
                className="checkout-input"
                value={billingAddress.city}
                onChange={handleBillingChange}
                placeholder="Enter city"
                required
                disabled={sameAsShipping}
              />
            </div>
            <div className="input-group state-zip-group">
              <div className="state-zip-field">
                <label htmlFor="billingRegion" className="input-label">
                  State <span className="required-asterisk">*</span>
                </label>
                <select
                  id="billingRegion"
                  name="region"
                  className="checkout-input state-zip-input"
                  value={billingAddress.region}
                  onChange={handleBillingChange}
                  required
                  disabled={sameAsShipping}
                >
                  {usStates.map((stateOption) => (
                    <option key={stateOption} value={stateOption}>
                      {stateOption}
                    </option>
                  ))}
                </select>
              </div>
              <div className="state-zip-field">
                <label htmlFor="billingZip" className="input-label">
                  ZIP Code <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="billingZip"
                  name="zip"
                  className="checkout-input zip-input"
                  value={billingAddress.zip}
                  onChange={handleBillingChange}
                  placeholder="Enter ZIP code (e.g., 12345)"
                  required
                  disabled={sameAsShipping}
                />
              </div>
            </div>
            <div className="input-group">
              <label htmlFor="billingCountry" className="input-label">
                Country
              </label>
              <input
                type="text"
                id="billingCountry"
                name="country"
                className="checkout-input"
                value={billingAddress.country}
                onChange={handleBillingChange}
                disabled
              />
            </div>
            <div className="input-group">
              <label htmlFor="cardNumber" className="input-label">
                Card Number <span className="required-asterisk">*</span>
              </label>
              <CardNumberElement id="cardNumber" className="card-element" />
            </div>
            <div className="input-group card-details-group">
              <div className="card-detail">
                <label htmlFor="cardExpiry" className="input-label">
                  Expiration <span className="required-asterisk">*</span>
                </label>
                <CardExpiryElement id="cardExpiry" className="card-element" />
              </div>
              <div className="card-detail">
                <label htmlFor="cardCvc" className="input-label">
                  CVC <span className="required-asterisk">*</span>
                </label>
                <CardCvcElement id="cardCvc" className="card-element" />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Order Summary</h3>
            <div className="summary-group">
              <p className="summary-label">Subtotal:</p>
              <p className="summary-value">${calculateSubtotal().toFixed(2)}</p>
            </div>
            <div className="summary-group">
              <p className="summary-label">Shipping:</p>
              <p className="summary-value">
                ${shippingOptions.standard.cost.toFixed(2)}
              </p>
            </div>
            <div className="summary-group">
              <p className="summary-label">Tax:</p>
              <p className="summary-value">
                ${(taxCalculation.tax_amount_exclusive / 100).toFixed(2)}
              </p>
            </div>
            <div className="summary-group total-group">
              <p className="summary-label">Total:</p>
              <p className="summary-value">${calculateTotal()}</p>
            </div>
            <div className="button-container">
              <button
                type="button"
                className="checkout-button"
                onClick={handleBuyNow}
                disabled={processing || !stripe || !elements}
              >
                {processing ? "Processing..." : "Buy Now"}
              </button>
            </div>
          </div>
        </form>
        {paymentError && <div className="checkout-error">{paymentError}</div>}
      </div>
      {processing && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

const CheckoutStep2Wrapper = ({ setCart }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeError, setStripeError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    getStripePromise()
      .then((promise) => {
        if (isMounted) setStripePromise(promise);
      })
      .catch(() => {
        if (isMounted) {
          setStripeError(
            "The secure checkout form could not be loaded. Please refresh the page or contact info@bonobohope.org."
          );
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (stripeError) {
    return <div className="checkout-error">{stripeError}</div>;
  }

  if (!stripePromise) {
    return <div className="checkout-container">Loading secure checkout...</div>;
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutStep2 setCart={setCart} />
    </Elements>
  );
};

export default CheckoutStep2Wrapper;
